import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { toLower } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { EMPTY_VALUES } from 'constants/general';
import { gtmDashboardSectionNames, gtmFilterNames } from 'features/views/dashboard/constants';
import { useButtonClickDataLayer, usePlusMoreButtonClickDataLayer } from 'features/views/dashboard/hooks';
import { toggleButtonGroupValues } from 'features/views/dashboard/leadsOverview/constants';
import { getToggleValue } from 'features/views/dashboard/leadsOverview/selectors';
import { quickFilter } from 'features/views/marketPlace/constants';
import { generateLeadIcon } from 'features/views/marketPlace/utils';
import { getRoutePath } from 'utils/routeHelper';
import { generateFullNameOrBusinessName } from 'utils/string';

import PlusMore from '../../MarketPlace/components/PlusMoreButton';

export const Lead = ({ lead }) => {
	const navigate = useNavigate();

	const pushToClickPlusMoreButtonDataLayer = usePlusMoreButtonClickDataLayer(lead?.name);
	const pushToClickButtonDataLayer = useButtonClickDataLayer();

	const leads = lead?.leads || EMPTY_VALUES.ARRAY;
	const originalLength = leads.length;

	const leadsToShow = leads.slice(0, 2);
	const plusMoreValue = originalLength - 2;

	const toggleValue = useSelector(getToggleValue);

	const handleRedirectToDetail = useCallback(
		(leadId) => () => {
			const filterType =
				toggleValue === toggleButtonGroupValues.DRAFT ? gtmFilterNames.DRAFT : gtmFilterNames.CURRENT;

			pushToClickButtonDataLayer({
				value: gtmDashboardSectionNames.LEADS,
				button_text: toLower(lead?.name),
				filter_type: filterType,
			})();

			const { pathname, state } = getRoutePath('leadDetail', { leadId }, true);
			navigate(pathname, { state });
		},
		[navigate, toggleValue, lead?.name, pushToClickButtonDataLayer],
	);

	const handleClickMore = useCallback(() => {
		const filterType =
			toggleValue === toggleButtonGroupValues.DRAFT ? gtmFilterNames.DRAFT : gtmFilterNames.CURRENT;
		pushToClickPlusMoreButtonDataLayer({
			value: gtmDashboardSectionNames.LEADS,
			filter_type: filterType,
		})();

		navigate({
			pathname: '/market-place',
			...(toggleValue === toggleButtonGroupValues.DRAFT && { search: `quickFilter=${quickFilter.IN_PROGRESS}` }),
		});
	}, [navigate, toggleValue, pushToClickPlusMoreButtonDataLayer]);

	return (
		<Paper sx={{ px: 3, py: 2, backgroundColor: (theme) => theme.palette.background[100], borderRadius: 1 }}>
			<Stack spacing={2} direction="row">
				{generateLeadIcon({
					taskTypeId: lead?.id,
					width: 56,
					height: 56,
				})}
				<Stack spacing={1}>
					<Typography variant="h6" color="text.primary">
						{lead?.name}
					</Typography>
					{!isNilOrEmpty(leadsToShow) &&
						leadsToShow.map((item) => (
							<Link
								key={item?.id}
								variant="body1"
								underline="none"
								onClick={handleRedirectToDetail(item?.id)}
								sx={{
									width: 'fit-content',
								}}
							>
								{generateFullNameOrBusinessName(item?.businessName, item?.surname, item?.firstName)}
							</Link>
						))}
					<PlusMore value={plusMoreValue} onClick={handleClickMore} />
				</Stack>
			</Stack>
		</Paper>
	);
};

Lead.propTypes = {
	lead: PropTypes.shape({
		id: PropTypes.number,
		name: PropTypes.string,
		priority: PropTypes.number,
		leads: PropTypes.array,
	}),
};

export default Lead;
